import React, { useState, useEffect, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import Fuse from 'fuse.js'

import { AutoCompleteField, CheckboxField, DropDownField } from "../helpers"

import { raiseToast } from "../services/redux/reducers/toast/toast"
import { fetchMaterialCategories, fetchMaterialBrands, fetchMaterialSpecifications } from "../services/redux/reducers/commonThunk"
import { getStructuredProductMaterialBrands } from "../services/api/materialSpecifications"


const StructuredProductSearch = (props) => {
    const { initialFilter, structuredProductFilter, setStructuredProductFilter, formErrors, filteredBrands, setFilteredBrands } = props

    const dispatch = useDispatch()

    const [searchTerms, setSearchTerms] = useState({})
    const [customInputs, setCustomInputs] = useState({})
    const [productSuggestions, setProductSuggestions] = useState([])
    const [filteredProducts, setFilteredProducts] = useState([])
    const [isBrandLoading, setIsBrandLoading] = useState(false)
    const [variantOptions, setVariantOptions] = useState([])
    const [attributes, setAttributes] = useState([])

    const materialCategories = useSelector((state) => state.common.materialCategories)
    const materialBrands = useSelector((state) => state.common.materialBrands)
    const materialSpecifications = useSelector(state => state.common.materialSpecifications)

    useEffect(() => {
        dispatch(fetchMaterialCategories())
        dispatch(fetchMaterialBrands())
        dispatch(fetchMaterialSpecifications())
    }, [])

    useEffect(() => {
        if (structuredProductFilter.selectedCategoryId) {
            const filteredMaterialSpecifications = materialSpecifications.filter(eachSpec => eachSpec.categoryId === structuredProductFilter.selectedCategoryId)
            if(filteredMaterialSpecifications.length === 0){
                dispatch(raiseToast({ severity: 'error', summary: "filtered Product", detail: 'No product found for the selected category.', life: 3000 }))
            }
            setFilteredProducts(filteredMaterialSpecifications)
        }
    }, [structuredProductFilter.selectedCategoryId])

    useEffect(() => {
        if (!Object.keys(initialFilter).length) {
          setStructuredProductFilter(prev => ({
            ...prev,
            selectedCategoryId: null,
            selectedProduct: null,
            selectedVariant: null,
            selectedBrand: null,
            selectedAttributes: {}
          }))
          setFilteredBrands([])
          setAttributes([])
          setFilteredProducts([])
          setVariantOptions([])
        } else {
          setStructuredProductFilter(prev => ({
            ...prev,
            selectedCategoryId: initialFilter.selectedCategoryId,
            selectedProduct: initialFilter.selectedProduct,
            selectedVariant: initialFilter.selectedVariant,
            selectedBrand: initialFilter.selectedBrand,
            selectedAttributes: initialFilter.selectedAttributes
          }))
        }
      }, [initialFilter])

    useEffect(() => {
        if (structuredProductFilter.selectedProduct) {
            const relatedVariants = materialSpecifications.filter(
                eachSpec => eachSpec.productName === structuredProductFilter.selectedProduct.productName
            )
            setVariantOptions(relatedVariants)
        } else {
            setVariantOptions([])
        }
        setAttributes([])
    }, [structuredProductFilter.selectedProduct])

    useEffect(() => {
        if (structuredProductFilter.selectedProduct && structuredProductFilter.selectedVariant) {
            const relatedSpecs = materialSpecifications.filter(
                eachSpec => eachSpec.productName === structuredProductFilter.selectedProduct.productName && eachSpec.variantName === structuredProductFilter.selectedVariant.variantName
            )

            const attributeMap = {}

            relatedSpecs.forEach(eachSpec => {
                const attrName = eachSpec.attribute
                if (!attrName) return

                if (!attributeMap[attrName]) {
                    attributeMap[attrName] = {
                        values: [],
                        attributeUnit: eachSpec.attributeUnit || null
                    }
                }

                if (eachSpec.filterConfig?.type === 'list') {
                    attributeMap[attrName].values.push(...eachSpec.filterConfig.values)
                } else if (eachSpec.filterConfig?.type === 'range') {
                    attributeMap[attrName].values.push(...eachSpec.filterConfig.values.map(r => `${r[0]} - ${r[1]}`))
                }
            })


            const finalAttributes = Object.entries(attributeMap).map(([attribute, data]) => ({
                attribute,
                attributeUnit: data.attributeUnit,
                values: [...new Set(data.values)]
            }))

            setAttributes(finalAttributes)

        } else {
            setAttributes([])
        }
    }, [structuredProductFilter.selectedBrand])

    const materialSpecFuzzSearch = useCallback(new Fuse(filteredProducts, {
        keys: ['productName'],
        includeScore: true,
        threshold: 0.5,
        ignoreLocation: true,
        shouldSort: true,
        getFn: (obj) => {
            return obj.productName.replace(/\s+/g, '')
        },
    }), [filteredProducts])

    const productSearchMethod = (event) => {
        if (event.query === "") {
            setProductSuggestions([...filteredProducts])
        }
        else {
            const searchResults = materialSpecFuzzSearch.search(event.query)
            const relatedProducts = searchResults.map(eachProduct => eachProduct.item)
            setProductSuggestions(relatedProducts)
        }
    }

    const handleProductChange = (e) => {
        setStructuredProductFilter(prev => ({
            ...prev,
            selectedProduct: e.target.value,
            selectedVariant: null,
            selectedAttributes: {},
            selectedBrand: null
        }))
        setFilteredBrands([])
    }

    const handleCategoryChange = (e) => {
        setStructuredProductFilter(prev => ({
            ...prev,
            selectedCategoryId: e.target.value,
            selectedProduct: null,
            selectedVariant: null,
            selectedBrand: null,
            selectedAttributes: {}
        }))
        setAttributes([])
        setFilteredBrands([])
    }

    const handleVarientChange = async (e) => {
        setStructuredProductFilter(prev => ({
            ...prev,
            selectedVariant: e.target.value,
            selectedAttributes: {},
            selectedBrand: null
        }))
        setAttributes([])
        setFilteredBrands([])

        setIsBrandLoading(true)
        const response = await getStructuredProductMaterialBrands({...structuredProductFilter, selectedVariant: e.target.value})
        setIsBrandLoading(false)
        if (response.failure) {
            return dispatch(raiseToast({ severity: 'error', summary: "Brand Data", detail: response.data.error, life: 3000 }))
        }
        if(response.data.length) {
            const brandIds = response.data
            const relatedBrands = materialBrands.filter(eachBrand => brandIds.includes(eachBrand.brandId))
            setFilteredBrands(relatedBrands)
            if (relatedBrands.length === 1) {
                setStructuredProductFilter(prev => ({ ...prev, selectedBrand: relatedBrands[0] }))
            }
        } else {
            setFilteredBrands([])
            dispatch(raiseToast({ severity: 'error', summary: "Brand Data", detail: "No related brands found.", life: 3000 }))
        }
    }

    const handleBrandChange = (e) => {
        setStructuredProductFilter(prev => ({ ...prev, selectedBrand: e.target.value }))
    }

    const handleAttributeValueChange = (attribute, value) => {
        setStructuredProductFilter(prev => {
            const updated = new Set([...(prev.selectedAttributes[attribute] || []), value])
            return {
                ...prev,
                selectedAttributes: {
                    ...prev.selectedAttributes,
                    [attribute]: Array.from(updated)
                }
            }
        })
    }

    const removeAttributeValue = (attribute, value) => {
        setStructuredProductFilter(prev => {
            const updated = (prev.selectedAttributes[attribute] || []).filter(v => v !== value)
            const updatedAttrs = { ...prev.selectedAttributes, [attribute]: updated }
            if (updated.length === 0) delete updatedAttrs[attribute]
            return {
                ...prev,
                selectedAttributes: updatedAttrs
            }
        })
    }

    const handleSearchChange = (attribute, value) => {
        setSearchTerms(prev => ({ ...prev, [attribute]: value }))
    }

    const handleRangeKeyDown = (e, attribute) => {
        if (e.key === 'Enter') {
            const min = customInputs[attribute].min
            const max = customInputs[attribute].max

            if (min !== '' && max !== '') {
                const minVal = parseFloat(min)
                const maxVal = parseFloat(max)
                const rangeLabel = minVal <= maxVal
                    ? `${minVal} - ${maxVal}`
                    : `${maxVal} - ${minVal}`

                handleAttributeValueChange(attribute, rangeLabel)

                setCustomInputs(prev => ({
                    ...prev,
                    [attribute]: {
                        ...prev[attribute],
                        min: '',
                        max: ''
                    }
                }))
            }
        }
    }

    const operatorSymbolMap = {
        gt: '>',
        gte: '>=',
        lt: '<',
        lte: '<=',
    }

    const handleOperatorKeyDown = (e, attribute) => {
        if (e.key === 'Enter' && customInputs[attribute]?.value?.trim()) {
            const selected = customInputs[attribute]?.type || 'text'
            const rawVal = customInputs[attribute].value.trim()

            let displayVal = rawVal

            if (operatorSymbolMap[selected]) {
                displayVal = `${operatorSymbolMap[selected]} ${rawVal}`
            }

            handleAttributeValueChange(attribute, displayVal)

            setCustomInputs(prev => ({
                ...prev,
                [attribute]: {
                    ...prev[attribute],
                    value: ''
                }
            }))
        }
    }

    const handleRemoveAttributeValue = (attribute, value) => {
        setStructuredProductFilter(prev => {
            const updated = (prev.selectedAttributes[attribute] || []).filter(v => v !== value)
            const updatedAttrs = { ...prev.selectedAttributes, [attribute]: updated }
            if (updated.length === 0) delete updatedAttrs[attribute]
            return {
                ...prev,
                selectedAttributes: updatedAttrs
            }
        })
    }
    
    const selectedAttributeTags = Object.entries(structuredProductFilter.selectedAttributes).map(([key, values]) => (
        <div key={key} className="m-1 px-2 py-1 bg-white border-1 border-200 border-round flex align-items-center gap-2 flex-wrap">
            <span className="font-bold text-blue-800 text-xs">{key.toUpperCase()}:</span>
            {values.map((eachValue, idx) => (
                <span
                    key={idx}
                    className="ml-1 flex items-center gap-1 text-xs px-2 py-1 border-round-2xl"
                    style={{ display: 'inline-flex', alignItems: 'center', background: "#e5e7eb" }}
                >
                    {eachValue}
                    <i
                        className="pi pi-times text-red-700 cursor-pointer ml-1"
                        style={{ fontSize: '0.6rem' }}
                        onClick={() => handleRemoveAttributeValue(key, eachValue)}
                    ></i>
                </span>
            ))}
        </div>
    ))
    

    return (
        <div className="structured-product-search" style={{
            margin: 0,
            padding: 0,
            maxHeight: '470px',
            overflowY: attributes.length ? 'auto' : 'hidden',
            overflowX: 'hidden'
        }}>
            <div className="grid mb-2">
                <div className="col-12 lg:col-3">
                    <DropDownField
                        name="category"
                        labelText="Category"
                        options={materialCategories.map(cat => ({ label: cat.categoryName, value: cat.categoryId }))}
                        value={structuredProductFilter.selectedCategoryId}
                        onChange={handleCategoryChange}
                        errorMessage={formErrors.selectedCategoryId}
                        required
                    />
                </div>
                <div className="col-12 lg:col-3">
                    <AutoCompleteField dropdown name="productName" labelText="Product" value={structuredProductFilter.selectedProduct} field="productName" dropdownButton searchMethod={productSearchMethod}
                        suggestions={[...new Map(productSuggestions.map(eachSpec => [eachSpec.productName, eachSpec])).values()]}
                        onChange={handleProductChange}
                        errorMessage={formErrors.selectedProduct}
                        required
                    />
                </div>
                <div className="col-12 lg:col-3">
                    <DropDownField
                        name="variantName"
                        labelText="Variant"
                        optionLabel="variantName"
                        options={[...new Map(variantOptions.map(opt => [opt.variantName, opt])).values()]}
                        value={structuredProductFilter.selectedVariant}
                        onChange={handleVarientChange}
                        errorMessage={formErrors.selectedVariant}
                        required
                    />
                </div>
                <div className="col-12 lg:col-3">
                    <DropDownField
                        name="brandName"
                        labelText="Brand"
                        optionLabel="brandName"
                        options={filteredBrands}
                        value={structuredProductFilter.selectedBrand}
                        onChange={handleBrandChange}
                        errorMessage={formErrors.selectedBrand}
                        loading={isBrandLoading}
                        disabled={isBrandLoading}
                        required
                    />
                </div>
            </div>

            <div className="divider-line mb-3"></div>

            {selectedAttributeTags.length > 0 && (
                <div className="mb-2 flex flex-wrap">
                    {selectedAttributeTags}
                </div>
            )}

            <div className="grid">
                {attributes.map(eachAttribute => {
                    const selectedVals = structuredProductFilter.selectedAttributes[eachAttribute.attribute] || []
                    const filteredOptions = eachAttribute.values.filter(eachValue =>
                        (searchTerms[eachAttribute.attribute] || "") === "" || eachValue.toLowerCase().includes(searchTerms[eachAttribute.attribute].toLowerCase())
                    )

                    return (
                        <div className="col-12 md:col-6 lg:col-3" key={eachAttribute.attribute}>
                            <div className=" text-900 font-medium mb-2 ml-1" style={{ fontSize: "13px"}}>
                                {eachAttribute.attribute.toUpperCase()}
                                {eachAttribute.attributeUnit ? ` (${eachAttribute.attributeUnit})` : ""}
                            </div>

                            <div className=" border-round-lg shadow-2 product-search-card">
                                <input
                                    type="text"
                                    value={searchTerms[eachAttribute.attribute] || ""}
                                    onChange={(e) => handleSearchChange(eachAttribute.attribute, e.target.value)}
                                    placeholder="Search..."
                                    className="p-inputtext p-component w-full px-3 py-2 border border-gray-300 rounded mb-2"
                                />

                                <div className="max-h-40 overflow-y-auto mb-2 px-1 checkbox-list-container" style={{ height: '80px' }}>
                                    {filteredOptions.length > 0 ? (
                                        filteredOptions.map((eachValue, i) => (
                                            <div key={i} className="flex align-items-center mb-1">
                                                <div className= "small-checkbox">
                                                <CheckboxField
                                                    label={eachValue}
                                                    checked={selectedVals.includes(eachValue)}
                                                    onChange={(e) => {
                                                        if (e.checked) handleAttributeValueChange(eachAttribute.attribute, eachValue)
                                                        else removeAttributeValue(eachAttribute.attribute, eachValue)
                                                    }}
                                                />
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-sm text-gray-500 italic">No Predefined options. You can still enter a custom value below.</div>
                                    )}
                                </div>
                                <div className="mt-2 flex gap-2 align-items-center">
                                    <select
                                        className="p-1 border border-gray-300 rounded"
                                        value={customInputs[eachAttribute.attribute]?.type || 'text'}
                                        style={{height: "34px", borderRadius: "5px"}}
                                        onChange={(e) =>
                                            setCustomInputs(prev => ({
                                                ...prev,
                                                [eachAttribute.attribute]: {
                                                    type: e.target.value,
                                                    value: '',
                                                    min: '',
                                                    max: ''
                                                }
                                            }))
                                        }
                                    >
                                        <option value="text">Text</option>
                                        <option value="range">Range</option>
                                        <option value="gt">&gt;</option>
                                        <option value="lt">&lt;</option>
                                        <option value="gte">&gt;=</option>
                                        <option value="lte">&lt;=</option>
                                    </select>

                                    {(() => {
                                        const selected = customInputs[eachAttribute.attribute]?.type || 'text'

                                        if (selected === 'range') {
                                            return (
                                                <div className="flex gap-2 w-full">
                                                    <input
                                                        type="number"
                                                        placeholder="Min"
                                                        value={customInputs[eachAttribute.attribute]?.min || ''}
                                                        onChange={(e) =>
                                                            setCustomInputs(prev => ({
                                                                ...prev,
                                                                [eachAttribute.attribute]: {
                                                                    ...prev[eachAttribute.attribute],
                                                                    min: e.target.value
                                                                }
                                                            }))
                                                        }
                                                        onKeyDown={(e) => handleRangeKeyDown(e, eachAttribute.attribute)}
                                                        className="p-inputtext p-component p-2 border border-dashed border-gray-400 rounded w-full"
                                                        style={{ height: '' }}
                                                    />
                                                    <input
                                                        type="number"
                                                        placeholder="Max"
                                                        value={customInputs[eachAttribute.attribute]?.max || ''}
                                                        onChange={(e) =>
                                                            setCustomInputs(prev => ({
                                                                ...prev,
                                                                [eachAttribute.attribute]: {
                                                                    ...prev[eachAttribute.attribute],
                                                                    max: e.target.value
                                                                }
                                                            }))
                                                        }
                                                        onKeyDown={(e) => handleRangeKeyDown(e, eachAttribute.attribute)}
                                                        className="p-inputtext p-component p-2 border border-dashed border-gray-400 rounded w-full"
                                                        style={{ height: '' }}
                                                    />
                                                </div>
                                            )
                                        } else {
                                            return (
                                                <input
                                                    type={selected === 'text' ? 'text' : 'number'}
                                                    placeholder={selected === 'text' ? 'Enter value' : `Value (${selected})`}
                                                    value={customInputs[eachAttribute.attribute]?.value || ''}
                                                    onChange={(e) =>
                                                        setCustomInputs(prev => ({
                                                            ...prev,
                                                            [eachAttribute.attribute]: {
                                                                ...prev[eachAttribute.attribute],
                                                                value: e.target.value
                                                            }
                                                        }))
                                                    }
                                                    onKeyDown={(e) => handleOperatorKeyDown(e, eachAttribute.attribute)}
                                                    className="p-inputtext p-component px-3 py-2 border border-dashed border-gray-400 rounded w-full"
                                                />
                                            )
                                        }
                                    })()}
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default StructuredProductSearch
